/**
 * Get the Look — Advanced Commerce fallback controller.
 *
 * For each [data-get-the-look] section on the page:
 *   1. Await window.__graphene (the Graphene `$g` ready promise from theme.liquid).
 *   2. Call $g.load with element id `pdp_recs_2`. The AC element template MUST
 *      emit a `<script type="application/json" data-ac-handles>` payload of the
 *      shape `{ "product_count": <n>, "handles": ["foo", "bar", ...] }`.
 *   3. Fetch up to 4 product cards in PARALLEL — one request per handle —
 *      via sections/api-card-product.liquid (`/products/{handle}?section_id=api-card-product`).
 *      Each response contains BOTH the desktop ("default") and mobile
 *      ("minimal") card variants; the client picks the appropriate one per
 *      breakpoint. Per-product addressing is required because Shopify's
 *      Section Rendering API strips arbitrary query params on this storefront,
 *      so a batched `?handles=a,b,c,d` call returns nothing.
 *   4. Inject cards into the desktop grid and mobile swiper, then re-init Swiper.
 *
 * Hide the entire section if AC returns no recommendations or errors.
 */
(() => {
  const SELECTOR = "[data-get-the-look]";
  const HANDLES_TARGET_SELECTOR = ".get-the-look-ac-handles";
  const DESKTOP_GRID_SELECTOR = ".get-the-look-ac-grid";
  const MOBILE_WRAPPER_SELECTOR = ".get-the-look-ac-mobile-wrapper";
  const HANDLES_PAYLOAD_SELECTOR =
    'script[type="application/json"][data-ac-handles]';
  const MAX_HANDLES = 4;

  class GetTheLook {
    constructor(root) {
      this.root = root;
      this.config = { ...root.dataset };
      this.controller = new AbortController();

      this.handlesTarget = root.querySelector(HANDLES_TARGET_SELECTOR);
      this.desktopGrid = root.querySelector(DESKTOP_GRID_SELECTOR);
      this.mobileWrapper = root.querySelector(MOBILE_WRAPPER_SELECTOR);

      if (!this.handlesTarget) return;

      this.#run();
    }

    destroy() {
      this.controller.abort();
    }

    async #run() {
      try {
        const $g = await window.__graphene;
        const handles = await this.#loadHandles($g);

        if (this.controller.signal.aborted) return;

        if (!handles.length) {
          this.#hide();
          return;
        }

        const responses = await Promise.all(
          handles.map((handle) => this.#fetchCard(handle)),
        );

        if (this.controller.signal.aborted) return;

        const usableResponses = responses.filter(
          (html) => typeof html === "string" && html.length > 0,
        );

        if (!usableResponses.length) {
          this.#hide();
          return;
        }

        this.#injectVariant(this.desktopGrid, usableResponses, "default", null);
        this.#injectVariant(
          this.mobileWrapper,
          usableResponses,
          "minimal",
          "swiper-slide",
        );

        const initFn =
          window.__getTheLookInit &&
          window.__getTheLookInit[this.config.sectionId];
        if (typeof initFn === "function") initFn();
      } catch (err) {
        if (err && err.name === "AbortError") return;
        this.#hide();
      }
    }

    #loadHandles($g) {
      return new Promise((resolve, reject) => {
        if (this.controller.signal.aborted) {
          reject(new DOMException("Aborted", "AbortError"));
          return;
        }

        const watchdog = setTimeout(() => {
          reject(new Error("Graphene $g.load timed out"));
        }, 8000);

        const settle =
          (fn) =>
          (...args) => {
            clearTimeout(watchdog);
            fn(...args);
          };

        $g.load({
          args: {
            shop_url: this.config.shopUrl,
            root_url: this.config.rootUrl,
            is_us_site: this.config.isUsSite === "true",
            low_stock_limit: Number(this.config.lowStockLimit) || 0,
          },
          page_context: {
            product_id: Number(this.config.productId),
            currency_id: this.config.currencyId,
            language_id: this.config.languageId,
          },
          elements: [{ id: "pdp_recs_2", target: HANDLES_TARGET_SELECTOR }],
          success: settle((response) => {
            if (this.controller.signal.aborted) {
              reject(new DOMException("Aborted", "AbortError"));
              return;
            }

            const elementResponse =
              response && response.elements
                ? Object.values(response.elements)[0]
                : null;

            if (elementResponse && elementResponse.product_count === 0) {
              this.#clearTarget();
              resolve([]);
              return;
            }

            const handles = this.#extractHandles().slice(0, MAX_HANDLES);
            this.#clearTarget();
            resolve(handles);
          }),
          error: settle((err) => {
            this.#clearTarget();
            reject(
              err instanceof Error ? err : new Error("Graphene $g.load failed"),
            );
          }),
        });
      });
    }

    #extractHandles() {
      const node = this.handlesTarget.querySelector(HANDLES_PAYLOAD_SELECTOR);
      if (!node) return [];

      try {
        const raw = node.textContent || "{}";
        const payload = JSON.parse(raw);
        const handles = Array.isArray(payload.handles) ? payload.handles : [];
        return handles.filter((h) => typeof h === "string" && h.length > 0);
      } catch {
        return [];
      }
    }

    #clearTarget() {
      if (this.handlesTarget) {
        this.handlesTarget.innerHTML = "";
      }
    }

    async #fetchCard(handle) {
      const base = this.config.cardsSectionUrl || "/";
      const separator = base.endsWith("/") ? "" : "/";
      const url = `${base}${separator}products/${encodeURIComponent(handle)}?section_id=api-card-product`;

      try {
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) return null;
        return await res.text();
      } catch (err) {
        if (err && err.name === "AbortError") throw err;
        return null;
      }
    }

    #injectVariant(container, htmls, variant, slideClass) {
      if (!container) return;

      this.#clearSkeletons(container);

      for (const html of htmls) {
        const template = document.createElement("template");
        template.innerHTML = html.trim();

        const variantNode = template.content.querySelector(
          `[data-variant="${variant}"]`,
        );

        if (!variantNode) continue;

        const wrapper = document.createElement("div");
        if (slideClass) wrapper.className = slideClass;

        while (variantNode.firstChild) {
          wrapper.appendChild(variantNode.firstChild);
        }

        container.appendChild(wrapper);
      }

      if (typeof window.initProductCardScroll === "function") {
        window.initProductCardScroll(container);
      }
    }

    #clearSkeletons(container) {
      const skeletons = container.querySelectorAll("[data-ac-skeleton]");
      for (const el of skeletons) el.remove();
    }

    #hide() {
      this.root.hidden = true;
    }
  }

  const instances = new WeakMap();

  const initAll = (scope = document) => {
    for (const root of scope.querySelectorAll(SELECTOR)) {
      if (instances.has(root)) continue;
      instances.set(root, new GetTheLook(root));
    }
  };

  const destroy = (root) => {
    const instance = instances.get(root);
    if (instance) {
      instance.destroy();
      instances.delete(root);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const sectionEl = document.getElementById(
      `shopify-section-${event.detail.sectionId}`,
    );
    if (sectionEl) initAll(sectionEl);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const sectionEl = document.getElementById(
      `shopify-section-${event.detail.sectionId}`,
    );
    if (!sectionEl) return;
    for (const root of sectionEl.querySelectorAll(SELECTOR)) {
      destroy(root);
    }
  });
})();
